import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Configuration/firebase";
import {
  isValidPhone,
  normalizePhone,
  startPhoneVerification,
  checkPhoneVerification,
} from "../utils/phone";
import "../CSS/App.css";

// Two-step phone verification required before using Safety Check-In or Events.
// Step 1: enter an international phone number, we text a code (Twilio Verify).
// Step 2: enter the code; on success the backend stamps phoneVerified on the
// user doc and we return the user to wherever the gate intercepted them.
const VerifyPhone = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from?.pathname || "/";

  const [step, setStep] = useState("phone"); // phone | code
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // If they already verified (e.g. opened this directly), skip ahead.
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), async (user) => {
      if (!user) {
        navigate("/login");
        return;
      }
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists() && snap.data().phoneVerified === true) {
        navigate(returnTo, { replace: true });
      }
    });
    return () => unsub();
  }, [navigate, returnTo]);

  const handleSendCode = async () => {
    if (!isValidPhone(phone)) {
      setError(
        "Enter a full international number, including country code (e.g. +14155552671).",
      );
      return;
    }
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await startPhoneVerification(phone);
      setStep("code");
      setInfo(`We sent a code to ${normalizePhone(phone)}.`);
    } catch (err) {
      console.error("Error sending verification code:", err);
      setError(
        err.message ||
          "Couldn't send a code right now. Please try again shortly.",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (code.trim().length < 4) {
      setError("Enter the code we texted you.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await checkPhoneVerification(phone, code);
      if (result.verified) {
        navigate(returnTo, { replace: true });
      } else {
        setError("That code didn't match. Please try again.");
      }
    } catch (err) {
      console.error("Error verifying code:", err);
      setError(err.message || "Couldn't verify that code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="container"
      style={{
        width: "80%",
        maxWidth: "400px",
        margin: "auto",
        paddingTop: "calc(20px + env(safe-area-inset-top, 0px))",
      }}
    >
      <h2>Verify your phone</h2>
      <p style={{ color: "var(--text-secondary, #777)", fontSize: "14px" }}>
        Safety Check-In and Events need a verified phone number so alerts and
        invites reach a real number. This is a one-time step.
      </p>

      {step === "phone" ? (
        <>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 415 555 2671"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px 15px",
              borderRadius: "12px",
              border: "1px solid var(--border-color, #ccc)",
              backgroundColor: "var(--input-bg, #fff)",
              color: "var(--text-primary, #000)",
              marginBottom: "12px",
              fontSize: "16px",
            }}
          />
          {error && <p style={{ color: "red" }}>{error}</p>}
          <button
            type="button"
            onClick={handleSendCode}
            disabled={busy}
            style={{
              padding: "10px 24px",
              border: "none",
              cursor: busy ? "default" : "pointer",
              borderRadius: "5px",
              backgroundColor: "#4CAF50",
              color: "white",
            }}
          >
            {busy ? "Sending..." : "Send Code"}
          </button>
        </>
      ) : (
        <>
          {info && (
            <p style={{ color: "var(--text-secondary, #777)", fontSize: "14px" }}>
              {info}
            </p>
          )}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            placeholder="123456"
            maxLength={8}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "12px 15px",
              fontSize: "22px",
              letterSpacing: "4px",
              textAlign: "center",
              borderRadius: "12px",
              border: "1px solid var(--border-color, #ccc)",
              backgroundColor: "var(--input-bg, #fff)",
              color: "var(--text-primary, #000)",
              marginBottom: "12px",
            }}
          />
          {error && <p style={{ color: "red" }}>{error}</p>}
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              type="button"
              onClick={handleVerify}
              disabled={busy}
              style={{
                padding: "10px 24px",
                border: "none",
                cursor: busy ? "default" : "pointer",
                borderRadius: "5px",
                backgroundColor: "#4CAF50",
                color: "white",
              }}
            >
              {busy ? "Verifying..." : "Verify"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("phone");
                setCode("");
                setError("");
                setInfo("");
              }}
              style={{
                backgroundColor: "transparent",
                color: "var(--text-primary, #333)",
                padding: "10px 15px",
                border: "1px solid var(--border-color, #ccc)",
                cursor: "pointer",
                borderRadius: "5px",
              }}
            >
              Change number
            </button>
          </div>
        </>
      )}

      <button
        type="button"
        onClick={() => navigate("/")}
        style={{
          background: "none",
          border: "none",
          color: "var(--text-secondary, #777)",
          cursor: "pointer",
          marginTop: "18px",
          padding: 0,
        }}
      >
        Not now
      </button>
    </div>
  );
};

export default VerifyPhone;
